import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { catchError, of } from 'rxjs';

import { FrontendComparisonSummary } from '../../../core/api/api.models';
import { AssetUrlPipe } from '../../../core/api/asset-url.pipe';
import { SavingArenaApiService } from '../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../core/seo/seo.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../core/seo/breadcrumb-trail';
import { itemListSchema, webPageSchema } from '../../../core/seo/structured-data';
import { Breadcrumb } from '../../../shared/breadcrumb/breadcrumb';

/** A category filter chip with the number of comparisons behind it. */
interface CategoryChip {
  slug: string;
  name: string;
  count: number;
}

/**
 * `/compare` — the comparison index.
 *
 * This exists for two concrete reasons, not decoration:
 *  1. Every comparison page's breadcrumb has a "Comparisons" crumb pointing here. Without the route
 *     that crumb — and the BreadcrumbList schema built from it — pointed at a 404.
 *  2. Comparison pages otherwise had no crawlable parent. A hub gives them internal links from a
 *     single indexable page instead of relying on the store-page cards alone (spec §12).
 */
@Component({
  selector: 'app-compare-hub-page',
  imports: [Breadcrumb, RouterLink, AssetUrlPipe],
  templateUrl: './compare-hub-page.html',
  styleUrl: './compare-hub-page.scss',
})
export class CompareHubPage {
  /**
   * "Comparisons", matching what the schema already said. The visible trail used to read "Compare
   * Stores" while the BreadcrumbList said "Comparisons" — the same crumb under two names.
   */
  protected readonly crumbs = breadcrumbTrail({ label: 'Comparisons' });

  private readonly api = inject(SavingArenaApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);

  protected readonly comparisons = signal<FrontendComparisonSummary[]>([]);
  protected readonly isLoading = signal(true);

  protected readonly search = signal('');
  protected readonly activeCategory = signal('');

  protected readonly comparisonCount = computed(() => this.comparisons().length);

  /** Distinct stores across all comparisons — a headline stat for the hero. */
  protected readonly storeCount = computed(() => {
    const ids = new Set<string>();
    for (const row of this.comparisons()) {
      if (row.storeA?.id) ids.add(row.storeA.id);
      if (row.storeB?.id) ids.add(row.storeB.id);
    }
    return ids.size;
  });

  /** Most recent update across the listed comparisons. */
  protected readonly lastUpdated = computed(() => {
    const times = this.comparisons()
      .map((row) => (row.updatedAt ? new Date(row.updatedAt).getTime() : 0))
      .filter((time) => time > 0);
    if (!times.length) return '';

    return new Date(Math.max(...times)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  });

  /**
   * The lead card. The most-compared pair by combined coupon count, which is the one most likely to
   * be useful — not an editorial pick, because there is no "featured" flag to honour.
   */
  protected readonly featured = computed(() => {
    const rows = this.comparisons();
    if (!rows.length) return null;
    return [...rows].sort((a, b) => b.popularity - a.popularity)[0];
  });

  /** Chips built from the listed comparisons, so a chip never leads to an empty grid. */
  protected readonly categories = computed<CategoryChip[]>(() => {
    const counts = new Map<string, CategoryChip>();

    for (const row of this.comparisons()) {
      const category = row.category;
      if (!category?.slug) continue;

      const existing = counts.get(category.slug);
      if (existing) existing.count += 1;
      else counts.set(category.slug, { slug: category.slug, name: category.name, count: 1 });
    }

    return [...counts.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  });

  /** Search + category filter, sorted by popularity. */
  protected readonly visible = computed(() => {
    const term = this.search().trim().toLowerCase();
    const category = this.activeCategory();

    const filtered = this.comparisons().filter((row) => {
      if (category && row.category?.slug !== category) return false;
      if (!term) return true;

      const haystack = [row.storeA?.name, row.storeB?.name, row.category?.name, row.title]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });

    return filtered.sort((a, b) => b.popularity - a.popularity);
  });

  protected readonly resultLabel = computed(() => {
    const count = this.visible().length;
    return `Showing ${count} comparison${count === 1 ? '' : 's'}`;
  });

  constructor() {
    this.api
      .getComparisons(undefined, 50)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        this.isLoading.set(false);
        const rows = response?.success ? response.data : [];
        this.comparisons.set(rows);
        this.applySeo(rows);
      });
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected selectCategory(slug: string): void {
    this.activeCategory.set(this.activeCategory() === slug ? '' : slug);
  }

  private applySeo(rows: FrontendComparisonSummary[]): void {
    const title = 'Store Comparisons: Compare Coupons, Discounts & Deals | SavingArena';
    const description =
      'Compare popular stores side by side: coupon availability, best and average discounts, shipping offers, cashback and overall savings.';

    this.seo.apply({ title, description, canonicalPath: '/compare' });

    this.seo.setJsonLd([
      webPageSchema({
        origin: this.seo.origin,
        url: this.seo.absolute('/compare'),
        name: title,
        description,
      }),
      breadcrumbSchemaFor(this.crumbs, (p) => this.seo.absolute(p)),
      // Only emit the list when there is something in it — an empty ItemList is noise.
      rows.length
        ? itemListSchema(
            rows.map((row) => ({
              '@type': 'WebPage',
              name: row.title,
              url: this.seo.absolute(row.path),
            })),
            'Store comparisons',
          )
        : null,
    ]);
  }

  /** Compact coupon count for the card stat ("1.2K"). */
  protected compact(value: number | undefined): string {
    const n = Number(value ?? 0);
    if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1).replace(/\.0$/, '')}K`;
    return String(n);
  }

  protected initials(name: string): string {
    return (name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('');
  }
}
